import { openDB, type IDBPDatabase } from 'idb';
import type { AppState } from '@autoday/core';
import { migrate } from '@autoday/core';
import { diffState, metaOf, StaleStateError, type DeviceState, type MetaRecord, type OutboxOp, type StorageAdapter } from './storage.ts';

const DB_NAME = 'autoday';
/** IndexedDB-schemaversion (separat från domänens schemaVersion). Uppgraderingar nedan är versionerade. */
const DB_VERSION = 1;
/**
 * Generationsräknare (nyckeln 'gen' i 'meta' respektive 'device'). Varje skrivning kontrollerar och
 * räknar upp den i SAMMA transaktion. Läs-skriv-transaktioner över samma lager körs i tur och ordning
 * även mellan flikar, så en flik med inaktuell bild av datan kan aldrig skriva tillbaka den.
 * Saknad nyckel (databaser från före räknaren) = 0.
 */
const GEN = 'gen';

type Stores = 'meta' | 'events' | 'sourceItems' | 'overlays' | 'templates' | 'outbox' | 'device';

export class IndexedDbStorage implements StorageAdapter {
  readonly kind = 'indexeddb' as const;
  private dbp: Promise<IDBPDatabase>;
  readonly name: string;
  private factory: IDBFactory | undefined;
  /** Senast lästa/skrivna generation. null = ännu inte läst: ingen kontroll (t.ex. första skrivning). */
  private gen: number | null = null;
  private devGen: number | null = null;
  private channel: BroadcastChannel | null = null;

  constructor(name = DB_NAME, factory?: IDBFactory) {
    this.name = name;
    this.factory = factory;
    const opts = {
      upgrade(db: IDBPDatabase, oldVersion: number) {
        if (oldVersion < 1) {
          db.createObjectStore('meta');
          db.createObjectStore('events', { keyPath: 'id' });
          db.createObjectStore('sourceItems', { keyPath: 'key' });
          db.createObjectStore('overlays', { keyPath: 'key' });
          db.createObjectStore('templates', { keyPath: 'id' });
          db.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true });
          db.createObjectStore('device');
        }
        // if (oldVersion < 2) { ... } – framtida migreringar
      },
    };
    if (factory) {
      // Tester: injicerad IDBFactory (fake-indexeddb).
      const g = globalThis as { indexedDB?: IDBFactory };
      const prev = g.indexedDB;
      g.indexedDB = factory;
      this.dbp = openDB(name, DB_VERSION, opts);
      g.indexedDB = prev;
    } else {
      this.dbp = openDB(name, DB_VERSION, opts);
    }
  }

  async load(): Promise<AppState | null> {
    const db = await this.dbp;
    const tx = db.transaction(['meta', 'events', 'sourceItems', 'overlays', 'templates'], 'readonly');
    const [meta, gen] = await Promise.all([tx.objectStore('meta').get('meta') as Promise<MetaRecord | undefined>, tx.objectStore('meta').get(GEN) as Promise<number | undefined>]);
    this.gen = gen ?? 0;
    if (!meta) return null;
    const [events, sourceItems, overlays, templates] = await Promise.all([
      tx.objectStore('events').getAll(),
      tx.objectStore('sourceItems').getAll(),
      tx.objectStore('overlays').getAll(),
      tx.objectStore('templates').getAll(),
    ]);
    await tx.done;
    return migrate({
      ...meta,
      events: Object.fromEntries(events.map((e) => [e.id, e])),
      sourceItems: Object.fromEntries(sourceItems.map((s) => [s.key, s])),
      overlays: Object.fromEntries(overlays.map((o) => [o.key, o])),
      templates: Object.fromEntries(templates.map((t) => [t.id, t])),
    } as AppState);
  }

  async persist(prev: AppState | null, next: AppState, outbox: OutboxOp[] = []): Promise<void> {
    const db = await this.dbp;
    const diff = diffState(prev, next);
    const stores: Stores[] = ['meta', 'events', 'sourceItems', 'overlays', 'templates', 'outbox'];
    const tx = db.transaction(stores, 'readwrite');
    const current = ((await tx.objectStore('meta').get(GEN)) as number | undefined) ?? 0;
    if (this.gen !== null && current !== this.gen) {
      tx.abort();
      await tx.done.catch(() => undefined);
      throw new StaleStateError();
    }
    const ops: Promise<unknown>[] = [];
    try {
    ops.push(tx.objectStore('meta').put(current + 1, GEN));
    if (diff.meta) ops.push(tx.objectStore('meta').put(metaOf(next), 'meta'));
    for (const [store, part] of [
      ['events', diff.events],
      ['sourceItems', diff.sourceItems],
      ['overlays', diff.overlays],
      ['templates', diff.templates],
    ] as const) {
      const os = tx.objectStore(store);
      for (const v of part.put) ops.push(os.put(v));
      for (const k of part.del) ops.push(os.delete(k));
    }
    for (const o of outbox) ops.push(tx.objectStore('outbox').add(o));
    } catch (e) {
      // Ett synkront fel (t.ex. ogiltig nyckel) avbryter INTE transaktionen av sig själv – gör det
      // uttryckligen så att redan köade skrivningar inte committas (allt eller inget).
      // Redan köade skrivningar avvisas av avbrottet – de är förväntade och hanteras här, inte ohanterade.
      for (const p of ops) p.catch(() => undefined);
      tx.abort();
      await tx.done.catch(() => undefined);
      throw e;
    }
    // Om ett put misslyckas asynkront avbryts hela transaktionen och tx.done avvisas.
    await Promise.all([...ops, tx.done]);
    this.gen = current + 1;
    this.notify('state');
  }

  async loadDevice(): Promise<DeviceState | null> {
    const db = await this.dbp;
    const tx = db.transaction('device', 'readonly');
    const [d, gen] = await Promise.all([tx.store.get('device') as Promise<DeviceState | undefined>, tx.store.get(GEN) as Promise<number | undefined>]);
    await tx.done;
    this.devGen = gen ?? 0;
    return d ?? null;
  }
  async saveDevice(d: DeviceState): Promise<void> {
    const db = await this.dbp;
    const tx = db.transaction('device', 'readwrite');
    const current = ((await tx.store.get(GEN)) as number | undefined) ?? 0;
    if (this.devGen !== null && current !== this.devGen) {
      tx.abort();
      await tx.done.catch(() => undefined);
      throw new StaleStateError();
    }
    await Promise.all([tx.store.put(d, 'device'), tx.store.put(current + 1, GEN), tx.done]);
    this.devGen = current + 1;
    this.notify('device');
  }

  /** Andra flikar med samma databas meddelas efter varje skrivning (bara en signal – inget innehåll). */
  private notify(what: 'state' | 'device') {
    try {
      if (!this.channel && typeof BroadcastChannel === 'function') {
        this.channel = new BroadcastChannel('autoday-data:' + this.name);
        (this.channel as BroadcastChannel & { unref?: () => void }).unref?.();
      }
      this.channel?.postMessage(what);
    } catch {
      /* Saknas BroadcastChannel skyddar generationsräknaren ändå mot överskrivning. */
    }
  }

  onExternalChange(cb: (what: 'state' | 'device') => void): () => void {
    if (typeof BroadcastChannel !== 'function') return () => undefined;
    const bc = new BroadcastChannel('autoday-data:' + this.name);
    (bc as BroadcastChannel & { unref?: () => void }).unref?.();
    bc.onmessage = (e: MessageEvent) => {
      if (e.data === 'state' || e.data === 'device') cb(e.data);
    };
    return () => bc.close();
  }
  async outbox(): Promise<OutboxOp[]> {
    const db = await this.dbp;
    return (await db.getAll('outbox')) as OutboxOp[];
  }
  async removeOutbox(seqs: number[]): Promise<void> {
    const db = await this.dbp;
    const tx = db.transaction('outbox', 'readwrite');
    await Promise.all([...seqs.map((s) => tx.store.delete(s)), tx.done]);
  }
  async wipe(): Promise<void> {
    const db = await this.dbp;
    const names = ['meta', 'events', 'sourceItems', 'overlays', 'templates', 'outbox', 'device'] as const;
    const tx = db.transaction([...names], 'readwrite');
    await Promise.all([...names.map((n) => tx.objectStore(n).clear()), tx.done]);
    // Tömda lager = generation 0. Andra flikar har högre generation och läser därför om före skrivning.
    this.gen = 0;
    this.devGen = 0;
  }
  async destroy(): Promise<void> {
    this.channel?.close();
    this.channel = null;
    (await this.dbp).close();
    const f = this.factory ?? indexedDB;
    await new Promise<void>((resolve, reject) => {
      const req = f.deleteDatabase(this.name);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error ?? new Error('Kunde inte radera den lokala databasen.'));
      // Blockerad av en annan öppen flik: den laddar om vid profilbytet och släpper databasen.
      req.onblocked = () => undefined;
    });
  }
}
